import React, { useState, useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";

import Login from "./Login";
import Navbar from "./Navbar";
import LandingPage from "./LandingPage";
import NewUserSignIn from "./components/Auth/NewUserSignIn";
import PrivateRoute from "./components/Auth/PrivateRoute";
import AdminRoute from "./components/Admin/AdminRoute";
import Footer from "./Footer";
import Cookies from "js-cookie";
import { ThemeProvider, createTheme, CssBaseline, IconButton } from "@mui/material";
import TnC from "./TnC";

// Lazy load heavy pages
const InventoryList = lazy(() => import("./components/Dashboard/CRUD/Inventory_list"));
const EditForm = lazy(() => import("./components/Dashboard/CRUD/Editform"));
const DashboardPageStatic = lazy(() => import("./DashboardPageStatic"));
const AdminComponent = lazy(() => import("./components/Admin/AdminComponent"));
const TransferItem = lazy(() => import("./components/Dashboard/CRUD/Transfer/TransferItem"));
const TransferLogs = lazy(() => import("./components/Report_and_Logs/TransferLogs"));
const LocationList = lazy(() => import("./components/Dashboard/CRUD/LocationList"));
const GenerateStickers = lazy(() => import("./components/Dashboard/CRUD/Qr_PrintOrder/GenerateStickers"));
const LocationViewer = lazy(() => import("./components/Location/LocationViewer"));
const LocationHierarchy = lazy(() => import("./components/Location/LocationHierarchy"));
const EditLocationForm = lazy(() => import("./components/Location/EditLocationForm"));
const InventoryExport = lazy(() => import("./components/Report_and_Logs/InventoryExport"));
const PublicItemDetails = lazy(() => import("./components/Dashboard/QRCode/PublicDetails"));
const PurchaseOrderList = lazy(() => import("./components/Dashboard/CRUD/PurChase_Orders/PurchaseOrderList"));
const AddressManagement = lazy(() => import("./components/Address/AddressManagement"));
const WarehouseManagement = lazy(() => import("./components/Warehouse/WarehouseManagement"));
const WarehouseDetails = lazy(() => import("./components/Warehouse/WarehouseDetails"));
const ProductionRecipe = lazy(() => import("./components/Production/ProductionRecipe"));
const ProductionDashboard = lazy(() => import("./components/Production/ProductionDashboard"));
const ProductionBatchRunner = lazy(() => import("./components/Production/ProductionBatchRunner"));
const RecipeList = lazy(() => import("./components/Production/RecipeList"));
const EditRecipe = lazy(() => import("./components/Production/EditRecipe"));
const ProductionHistory = lazy(() => import("./components/Production/ProductionHistory"));
const ProductionSimulation = lazy(() => import("./components/Production/ProductionSimulation"));
const StockReviewer = lazy(() => import("./components/StockReviewer/StockReviewer"));
const Catalogue = lazy(() => import("./components/Catalogue/Catalogue"));

function AppContent() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [mode, setMode] = useState('light');
  const location = useLocation();

  useEffect(() => {
    const token = Cookies.get("jwtToken");
    setIsLoggedIn(!!token);
  }, []);

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: mode === 'dark' ? '#90caf9' : '#1976d2',
      },
      background: {
        default: mode === 'dark' ? '#181a20' : '#f5f5f5',
        paper: mode === 'dark' ? '#23272f' : '#fff',
      },
    },
  });

  // Global background styles - matching dashboard
  const backgroundStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    zIndex: -1,
  };

  const isLandingPage = location.pathname === '/' || location.pathname === '/login';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Global Background Image */}
      <div style={backgroundStyles} />
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        {!isLandingPage && (
          <>
            <Navbar isLoggedIn={isLoggedIn} mode={mode} setMode={setMode} />
          </>
        )}
        <div style={{ flex: 1 }}>
          <Suspense fallback={<div style={{textAlign:'center',marginTop:40}}><span>Loading...</span></div>}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/new-user-sign-in" element={<NewUserSignIn />} />
              <Route path="/verify-otp" element={<Login />} />
              <Route path="/dashboard" element={<DashboardPageStatic />} />
              <Route path="/inventory_list" element={<InventoryList />} />
              <Route path="/editform/:id" element={<EditForm />} />
              <Route path="/transfer/:id" element={<TransferItem />} />
              <Route path="/transferLogs" element={<TransferLogs />} />
              <Route path="/location_list" element={<LocationList />} />
              <Route path="/location_viewer" element={<LocationViewer />} />
              <Route path="/location_hierarchy" element={<LocationHierarchy />} />
              <Route path="/edit_location/:id" element={<EditLocationForm />} />
              <Route path="/admin" element={<AdminRoute><AdminComponent /></AdminRoute>} />
              <Route path="/production_recipe" element={<ProductionRecipe />}/>
              <Route path="/sticker" element={<AdminRoute><GenerateStickers /></AdminRoute>} />
              <Route path="/reports" element={<AdminRoute><InventoryExport /></AdminRoute>} />
              <Route path="/publicdetails/:id" element={<PrivateRoute><PublicItemDetails /></PrivateRoute>} />
              <Route path="/purchase_order" element={<PurchaseOrderList />} />
              <Route path="/address" element={<PrivateRoute><AddressManagement /></PrivateRoute>} />
              <Route path="/warehouse" element={<PrivateRoute><WarehouseManagement /></PrivateRoute>} />
              <Route path="/warehouse/:warehouseId" element={<PrivateRoute><WarehouseDetails /></PrivateRoute>} />
              <Route path="/production_dashboard" element={<PrivateRoute><ProductionDashboard /></PrivateRoute>} />
              <Route path="/production_batch" element={<PrivateRoute><ProductionBatchRunner /></PrivateRoute>} />
              <Route path="/recipe_list" element={<PrivateRoute><RecipeList /></PrivateRoute>} />
              <Route path="/edit_recipe/:recipeId" element={<PrivateRoute><EditRecipe /></PrivateRoute>} />
              <Route path="/production_history" element={<PrivateRoute><ProductionHistory /></PrivateRoute>} />
              <Route path="/production_simulation" element={<PrivateRoute><ProductionSimulation /></PrivateRoute>} />
              <Route path="/product_review" element={<PrivateRoute><StockReviewer /></PrivateRoute>} />
              <Route path="/catalogue" element={<PrivateRoute><Catalogue /></PrivateRoute>} />
              <Route path="/terms" element={<TnC />} />
              <Route path="/privacy" element={<TnC />} />
            </Routes>
          </Suspense>
        </div>
        {!isLandingPage && <Footer />}
      </div>
    </ThemeProvider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
